import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { dict, type TKey } from "@/lib/i18n";
import {
  listSiteContent,
  saveSiteContent,
  resetSiteContent,
} from "@/lib/site-content.functions";

export const Route = createFileRoute("/admin/content")({
  component: AdminContent,
});

const GROUPS: Array<{ label: string; prefixes: string[] }> = [
  { label: "Ana sayfa", prefixes: ["index."] },
  { label: "Hakkımda", prefixes: ["about."] },
  { label: "Kitap", prefixes: ["book."] },
  { label: "Arşiv", prefixes: ["archive."] },
  { label: "Kategoriler", prefixes: ["cat."] },
  { label: "Menü & Footer", prefixes: ["nav.", "footer."] },
  { label: "Newsletter", prefixes: ["newsletter."] },
  { label: "Yazı sayfası & ortak", prefixes: ["essay.", "common."] },
  { label: "Arama", prefixes: ["search."] },
  { label: "SEO başlıkları & açıklamalar", prefixes: ["title.", "meta."] },
  { label: "Hata sayfaları", prefixes: ["err."] },
];

function groupOf(key: string): string {
  for (const g of GROUPS) {
    if (g.prefixes.some((p) => key.startsWith(p))) return g.label;
  }
  return "Diğer";
}

function AdminContent() {
  const list = useServerFn(listSiteContent);
  const save = useServerFn(saveSiteContent);
  const reset = useServerFn(resetSiteContent);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["admin-site-content"],
    queryFn: () => list(),
  });

  const overridesMap = useMemo(() => {
    const map: Record<string, { en: string; tr: string }> = {};
    for (const r of data?.rows ?? []) map[r.key] = { en: r.value_en, tr: r.value_tr };
    return map;
  }, [data]);

  const [edits, setEdits] = useState<Record<string, { en: string; tr: string }>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const allKeys = Object.keys(dict) as TKey[];
  const filtered = allKeys.filter((k) => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (
      (k as string).toLowerCase().includes(f) ||
      dict[k].tr.toLowerCase().includes(f) ||
      dict[k].en.toLowerCase().includes(f)
    );
  });

  const grouped = useMemo(() => {
    const g: Record<string, TKey[]> = {};
    for (const k of filtered) {
      const label = groupOf(k as string);
      (g[label] ||= []).push(k);
    }
    return g;
  }, [filtered]);

  const valueFor = (k: TKey, l: "en" | "tr") => {
    const key = k as string;
    if (edits[key]?.[l] !== undefined) return edits[key][l];
    if (overridesMap[key]) return overridesMap[key][l];
    return dict[k][l];
  };

  const isOverridden = (k: TKey) => !!overridesMap[k as string];
  const isDirty = (k: TKey) => !!edits[k as string];

  const setEdit = (k: TKey, l: "en" | "tr", v: string) => {
    const key = k as string;
    setEdits((prev) => {
      const cur = prev[key] ?? {
        en: overridesMap[key]?.en ?? dict[k].en,
        tr: overridesMap[key]?.tr ?? dict[k].tr,
      };
      return { ...prev, [key]: { ...cur, [l]: v } };
    });
  };

  const saveMut = useMutation({
    mutationFn: (k: TKey) =>
      save({
        data: {
          key: k as string,
          value_en: valueFor(k, "en"),
          value_tr: valueFor(k, "tr"),
        },
      }),
    onSuccess: async (_d, k) => {
      setSaving(null);
      setEdits((prev) => {
        const { [k as string]: _, ...rest } = prev;
        return rest;
      });
      await refetch();
    },
    onError: (e: Error) => {
      setSaving(null);
      alert("Kaydetme hatası: " + e.message);
    },
  });

  const resetMut = useMutation({
    mutationFn: (k: TKey) => reset({ data: { key: k as string } }),
    onSuccess: async (_d, k) => {
      setEdits((prev) => {
        const { [k as string]: _, ...rest } = prev;
        return rest;
      });
      await refetch();
    },
  });

  const onSave = (k: TKey) => {
    setSaving(k as string);
    saveMut.mutate(k);
  };

  if (isLoading) return <p className="text-sm text-ink/60">Yükleniyor…</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl">Site metinleri</h1>
          <p className="text-xs text-ink/70 mt-1">
            Her metnin Türkçe ve İngilizce sürümünü düzenleyin. "Kaydet" butonuna
            basılınca o anahtar canlı sitede güncellenir. "Varsayılana dön" override'ı kaldırır.
          </p>
        </div>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Anahtar veya metinde ara…"
          className="px-3 py-2 border border-border rounded bg-paper text-sm w-72"
        />
      </div>

      {Object.entries(grouped).map(([label, keys]) => (
        <section key={label} className="border border-border rounded">
          <h2 className="font-serif text-xl px-4 py-3 border-b border-border bg-ink/[0.02]">
            {label}{" "}
            <span className="text-xs text-ink/85 font-sans">({keys.length})</span>
          </h2>
          <ul className="divide-y divide-border">
            {keys.map((k) => (
              <li key={k as string} className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <code className="text-[11px] text-ink/70">{k as string}</code>
                  <div className="flex items-center gap-2">
                    {isOverridden(k) && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-clay">
                        özelleştirildi
                      </span>
                    )}
                    {isDirty(k) && (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-moss">
                        ● değişti
                      </span>
                    )}
                    <button
                      onClick={() => onSave(k)}
                      disabled={!isDirty(k) || saving === (k as string)}
                      className="text-xs px-3 py-1 bg-ink text-paper rounded-full disabled:opacity-30"
                    >
                      {saving === (k as string) ? "..." : "Kaydet"}
                    </button>
                    {isOverridden(k) && (
                      <button
                        onClick={() => resetMut.mutate(k)}
                        className="text-xs text-ink/85 hover:text-clay"
                      >
                        Varsayılana dön
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <label className="text-xs">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink/70">
                      Türkçe
                    </span>
                    <textarea
                      value={valueFor(k, "tr")}
                      onChange={(e) => setEdit(k, "tr", e.target.value)}
                      rows={Math.min(8, Math.max(2, Math.ceil(valueFor(k, "tr").length / 80)))}
                      className="w-full mt-1 px-3 py-2 border border-border rounded bg-paper text-sm font-serif"
                    />
                  </label>
                  <label className="text-xs">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-ink/70">
                      English
                    </span>
                    <textarea
                      value={valueFor(k, "en")}
                      onChange={(e) => setEdit(k, "en", e.target.value)}
                      rows={Math.min(8, Math.max(2, Math.ceil(valueFor(k, "en").length / 80)))}
                      className="w-full mt-1 px-3 py-2 border border-border rounded bg-paper text-sm font-serif"
                    />
                  </label>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
